'use client';

import { useTranslation } from '@i18n/useTranslation';
import { Filters } from '@server/careers-backend/search';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  createContext,
  ReactNode,
  startTransition,
  useActionState,
  useMemo,
  useState
} from 'react';

type FiltersContextProps = {
  isPending: boolean;
  getSelectedFilterOptions: (name: keyof Filters) => string[];
  appliedFilters: Record<keyof Filters | keyof Pagination, string[]>;
  setFilter: (category: keyof Filters, values: string[] | undefined) => void;
  setAllFilters: (filters: Record<keyof Filters | keyof Pagination, string[] | undefined>) => void;
  clearFilters: () => void;
  activePage: number;
  updateActivePage: (page: number) => void;
  isInternalUser: boolean;
};

const DEFAULT_PAGE = 1;

const initialState: FiltersContextProps = {
  isPending: false,
  getSelectedFilterOptions: () => [],
  appliedFilters: {} as Record<keyof Filters | keyof Pagination, string[]>,
  setFilter: () => {},
  setAllFilters: () => {},
  clearFilters: () => {},
  activePage: DEFAULT_PAGE,
  updateActivePage: () => {},
  isInternalUser: false
};

type FiltersContextProviderProps = {
  initialActivePage?: number;
  isInternalUser: boolean;
};

type Pagination = {
  page: string;
};

export const FiltersContext = createContext<FiltersContextProps>(initialState);

export const FiltersContextProvider = ({
  children,
  initialActivePage = DEFAULT_PAGE,
  isInternalUser
}: FiltersContextProviderProps & { children: ReactNode }) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { t } = useTranslation('filterPage');

  // An action  hook keeps track of the queue and executes them in order so the result of the previous call is passed
  const [_, pushSearchParamsAction, isPending] = useActionState(
    (
      prevState: URLSearchParams,
      payload: {
        newSearchParams: URLSearchParams;
      }
    ) => {
      const { newSearchParams } = payload;

      // No need to redirect if the search params are the same
      if (prevState.toString() === newSearchParams.toString()) {
        return prevState;
      }

      const newSearch = newSearchParams.toString();
      const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;

      router.push(newUrl, { scroll: false });

      return newSearchParams;
    },
    new URLSearchParams(searchParams.toString())
  );

  /* Preventing losing selected filters when selecting is faster than navigating */
  const [storedSearchParams, setStoredSearchParams] = useState(
    new URLSearchParams(searchParams.toString())
  );
  const [activePage, setActivePage] = useState(initialActivePage);

  const pageQueryParamKey = t('page');

  const setSearchParams = (
    filters: { key: keyof Filters | keyof Pagination; values: string[] | undefined }[] | null,
    replaceAll = false
  ) => {
    const baseSearch = replaceAll ? '' : storedSearchParams.toString();
    const params = new URLSearchParams(baseSearch);

    if (filters) {
      for (const { key, values } of filters) {
        params.delete(key);

        if (values && values.length > 0) {
          for (const value of values) {
            params.append(key, value);
          }
        }
      }
    }

    const newSearchParams = new URLSearchParams(params.toString());
    setStoredSearchParams(newSearchParams);

    startTransition(() => {
      pushSearchParamsAction({ newSearchParams });
    });
  };

  // Set a single filter category
  const setFilter = (category: keyof Filters, values: string[] | undefined) => {
    setActivePage(DEFAULT_PAGE);
    setSearchParams([
      { key: category, values },
      { key: pageQueryParamKey as keyof Pagination, values: [DEFAULT_PAGE.toString()] }
    ]);
  };

  // Set all filters at once
  const setAllFilters = (
    filters: Record<keyof Filters | keyof Pagination, string[] | undefined>
  ) => {
    const { page, ...filterValues } = filters;
    const pageValue = page ? Number(page[0]) : DEFAULT_PAGE;

    setActivePage(pageValue);
    setSearchParams(
      [
        ...Object.entries(filterValues).map(([key, values]) => ({
          key: key as keyof Filters,
          values
        })),
        { key: pageQueryParamKey as keyof Pagination, values: [pageValue.toString()] }
      ],
      true
    );
  };

  const getSelectedFilterOptions = (name: keyof Filters) => {
    return storedSearchParams.getAll(name).map(item => item.toLowerCase());
  };

  const appliedFilters = useMemo(() => {
    return Array.from(storedSearchParams).reduce(
      (acc, [key, value]) => {
        if (key === 'query') return acc;
        if (key === pageQueryParamKey) {
          acc.page = [value];
          return acc;
        }
        const filterKey = key as keyof Filters;
        acc[filterKey] = acc[filterKey] || [];
        acc[filterKey].push(value.toLowerCase());
        return acc;
      },
      {} as Record<keyof Filters | keyof Pagination, string[]>
    );
  }, [storedSearchParams, pageQueryParamKey]);

  const clearFilters = () => {
    setSearchParams(null, true);
    setActivePage(DEFAULT_PAGE);
  };

  const updateActivePage = (page: number) => {
    setActivePage(page);
    setSearchParams([{ key: pageQueryParamKey as keyof Pagination, values: [page.toString()] }]);
  };

  return (
    <FiltersContext.Provider
      value={{
        isPending,
        getSelectedFilterOptions,
        appliedFilters,
        setFilter,
        setAllFilters,
        clearFilters,
        activePage,
        updateActivePage,
        isInternalUser
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
